import { useId, useMemo, useState } from "react";
import type { TaxAddress } from "@townsy/contracts";
import { POSTAL_REQUIRED, countryOptions, taxAddressProblem } from "../../commerce/taxAddress";

/**
 * ADR-0046: "Where are you?" before the price is fixed. Sales tax depends on the buyer's location (Stripe Tax); only
 * the country and the postal code are asked for, and nothing is charged here. The server checks them again and
 * computes the tax; the browser never sends a tax amount.
 */
export function TaxAddressStep({
  initial,
  serverMessage,
  busy,
  onSubmit,
}: {
  initial: TaxAddress | null;
  /** The server's explanation after a refused location (TAX_ADDRESS_REQUIRED). */
  serverMessage: string | null;
  busy: boolean;
  onSubmit(address: TaxAddress): void;
}) {
  const countryId = useId();
  const postalId = useId();
  const hintId = useId();
  const options = useMemo(() => countryOptions(), []);
  const [country, setCountry] = useState(initial?.country ?? "US");
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? "");
  const [touched, setTouched] = useState(false);
  const problem = taxAddressProblem({ country, postalCode });
  const postalRequired = POSTAL_REQUIRED.has(country);

  return (
    <form
      className="stack"
      data-testid="checkout-tax-address"
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (problem || busy) return;
        onSubmit({ country, postalCode: postalCode.trim() ? postalCode.trim() : null });
      }}
    >
      <p style={{ margin: 0 }}>
        <strong>Where are you?</strong> Sales tax depends on your location. It is added to the price before you pay, and it is paid by card (Townsy
        Credit is never used for tax).
      </p>
      <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label htmlFor={countryId} className="field" style={{ flex: "2 1 200px" }}>
          Country
          <select id={countryId} value={country} onChange={(e) => setCountry(e.target.value)} data-testid="tax-country" autoComplete="country">
            {options.map((o) => (
              <option key={o.code} value={o.code}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor={postalId} className="field" style={{ flex: "1 1 120px" }}>
          {country === "US" ? "ZIP code" : "Postal code"}
          {postalRequired ? "" : " (optional)"}
          <input
            id={postalId}
            type="text"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            inputMode={country === "US" ? "numeric" : "text"}
            autoComplete="postal-code"
            maxLength={12}
            aria-describedby={hintId}
            aria-invalid={touched && problem !== null}
            data-testid="tax-postal-code"
          />
        </label>
      </div>
      <p id={hintId} className="hint" style={{ margin: 0 }}>
        Only your country and postal code are used, to calculate sales tax. Nothing is charged yet.
      </p>
      {touched && problem ? (
        <p className="small error-text" role="alert" style={{ margin: 0 }} data-testid="tax-address-problem">
          {problem}
        </p>
      ) : null}
      {serverMessage ? (
        <div className="notice notice-warning small" role="alert" data-testid="tax-address-server">
          {serverMessage}
        </div>
      ) : null}
      <div>
        <button type="submit" className="btn btn-primary" disabled={busy} data-testid="tax-address-continue">
          See the price with tax
        </button>
      </div>
    </form>
  );
}
